/**
 * AdminPage Component
 * 
 * This component provides a secure admin interface for managing DUI First Time Offender Program cases.
 * It uses Microsoft 365 (Azure AD / Entra ID) SSO authentication via MSAL.
 * 
 * Features:
 * - Microsoft 365 SSO authentication (Outlook / Office 365)
 * - Case listing with status-based priority sorting
 * - Real-time status updates for individual cases
 * - Color-coded status indicators for visual clarity
 * - Responsive design for various screen sizes
 * 
 * Authentication:
 * - Uses MSAL (@azure/msal-react) for Azure AD authentication
 * - Acquires bearer tokens with "access_as_user" scope for backend API calls
 * - Backend validates tokens via Microsoft.Identity.Web (JwtBearer)
 * 
 * @author DUI First Time Offender Program Team
 * @version 3.0 - Microsoft 365 SSO Implementation
 * @date February 2026
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { getApiTokenRequest, hasAppAdminRole } from '../config/authConfig';
import { API_ENDPOINTS } from '../config/api';
import { AdminAuthContext } from '../App';
import { COUNTY_OF_ARREST_OPTIONS } from '../config/constants';

const CASE_STATUS_OPTIONS = ['Pending', 'Denied', 'Success'];
const STATUS_TO_BACKEND = {
  Pending: 'Pending',
  Denied: 'Denied',
  Success: 'Success',
};

const AdminPage = () => {
  // ==================== AUTHENTICATION ====================
  const { adminUser, logoutAdmin } = useContext(AdminAuthContext);
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();

  const [authState, setAuthState] = useState('checking');
  const [authError, setAuthError] = useState('');

  const decodeJwtClaims = (token) => {
    if (!token || typeof token !== 'string') return null;
    const parts = token.split('.');
    if (parts.length < 2) return null;

    try {
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(payload);
      return JSON.parse(json);
    } catch {
      return null;
    }
  };

  /** Acquire an access token (silent first, redirect fallback) */
  const getAccessToken = useCallback(async (accountOverride) => {
    const account = accountOverride || instance.getActiveAccount() || accounts[0];
    if (!account) throw new Error('No authenticated account. Please sign in.');

    const loginRequest = getApiTokenRequest();

    try {
      const response = await instance.acquireTokenSilent({ ...loginRequest, account });
      return response;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        setAuthState('redirecting');
        await instance.acquireTokenRedirect({
          ...loginRequest,
          account,
          redirectStartPage: window.location.href,
        });
        return null;
      }

      throw error;
    }
  }, [instance, accounts]);
  
  // ==================== STATE MANAGEMENT ====================
  
  /**
   * Cases data state - holds all DUI cases from database
   * Sorted by status priority (Pending first, then others)
   * @type {Array<Object>}
   */
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  
  /**
   * Status updates tracking state
   * Maps case UniqueNumber to selected status for batch updates
   * @type {Object<string, string>}
   */
  const [statusUpdates, setStatusUpdates] = useState({});

  /**
   * Filter state for status filtering
   * Tracks the currently selected status filter
   * @type {string}
   */
  const [statusFilter, setStatusFilter] = useState('All');

  /**
   * Filter state for county of arrest filtering
   * @type {string}
   */
  const [countyFilter, setCountyFilter] = useState('All');

  /**
   * Notification popup state
   * { message, type: 'success' | 'error' }
   */
  const [notification, setNotification] = useState(null);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  // ==================== LIFECYCLE EFFECTS ====================
  
  /**
   * Effect hook to fetch cases when user is authenticated
   * Triggers case data loading after successful SSO authentication
   */
  useEffect(() => {
    let isMounted = true;

    const ensureAdminAccess = async () => {
      const account = instance.getActiveAccount() || accounts[0];

      if (!account) {
        setAuthState('redirecting');
        await instance.loginRedirect({
          ...getApiTokenRequest(),
          redirectStartPage: window.location.href,
        });
        return;
      }

      if (!instance.getActiveAccount()) {
        instance.setActiveAccount(account);
      }

      try {
        const tokenResponse = await getAccessToken(account);
        if (!tokenResponse) return;

        const accessTokenClaims = decodeJwtClaims(tokenResponse.accessToken);
        const authorized =
          hasAppAdminRole(accessTokenClaims) ||
          hasAppAdminRole(tokenResponse.idTokenClaims) ||
          hasAppAdminRole(account.idTokenClaims);

        if (!authorized) {
          if (isMounted) {
            setAuthState('denied');
          }
          return;
        }

        if (isMounted) {
          setAuthState('authorized');
        }

        await fetchCases(tokenResponse.accessToken);
      } catch (error) {
        console.error('Admin authentication error:', error);
        if (isMounted) {
          setAuthError(error.message || 'Authentication failed.');
          setAuthState('error');
        }
      }
    };

    ensureAdminAccess();

    return () => {
      isMounted = false;
    };
  }, [instance, accounts, getAccessToken]);

  // ==================== DATA FETCHING FUNCTIONS ====================
  
  /**
   * Fetches all cases from the database via API.
   *
   * API Endpoint: GET /case
   * Returns CaseDto[] and initializes status tracking per case number.
   */
  const fetchCases = async (accessToken) => {
    setCasesLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.ADMIN_CASES, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to load cases (HTTP ${response.status}).`);
      }

      const data = await response.json();
      setCases(data);
      
      // Initialize status updates state for each case
      const initialStatusUpdates = {};
      data.forEach((c) => {
        initialStatusUpdates[c.caseNumber] = c.status;
      });
      setStatusUpdates(initialStatusUpdates);
    } catch (err) {
      console.error('Error fetching cases:', err);
    } finally {
      setCasesLoading(false);
    }
  };

  // ==================== EVENT HANDLER FUNCTIONS ====================
  
  /**
   * Handles logout - signs out via MSAL and redirects
   */
  const handleLogout = async () => {
    await logoutAdmin();
    navigate('/');
  };

  /**
   * Handles status change for individual cases
   * Updates the local statusUpdates state to track pending changes
   * 
   * @param {string} caseNumber - Case number identifier
   * @param {string} newStatus - New status value selected by admin
   */
  const handleStatusChange = (caseNumber, newStatus) => {
    setStatusUpdates((prev) => ({
      ...prev,
      [caseNumber]: newStatus,
    }));
  };

  /**
   * Submits a status update for a specific case.
   *
   * API Endpoint: POST /case/status?caseNumber={}&status={}
   * Valid statuses are currently Pending, Denied, and Success.
   */
  const handleStatusSubmit = async (caseNumber, caseEmail) => {
    const newStatus = statusUpdates[caseNumber];
    const backendStatus = STATUS_TO_BACKEND[newStatus] || newStatus;
    try {
      const tokenResponse = await getAccessToken();
      if (!tokenResponse) return;

      const token = tokenResponse.accessToken;
      // Map UI status value to the backend status value before submitting.
      const url = API_ENDPOINTS.UPDATE_CASE_STATUS(caseNumber, backendStatus);
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        // Success: Refresh data and notify user
        await fetchCases(token); // Refresh the cases list to show updated status
        const emailNote = caseEmail ? ` Email notification sent to ${caseEmail}.` : ' Email notification sent to the applicant.';
        showNotification(`Status updated to "${newStatus}" successfully!${emailNote}`, 'success');
      } else {
        // Failure: Notify user of error
        showNotification('Failed to update status. Please try again.', 'error');
      }
    } catch (err) {
      // Network or server errors
      console.error('Status update error:', err);
      showNotification('Error updating status. Please check your connection.', 'error');
    }
  };

  // ==================== UTILITY FUNCTIONS ====================
  
  /**
   * Returns the badge color for the backend case status values.
   */
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending': return '#ffc107'; // Yellow - High priority
      case 'Success': return '#28a745'; // Green - Completed
      case 'Denied': return '#dc3545'; // Red - Denied
      default: return '#6c757d'; // Gray - Unknown/Default
    }
  };

  /**
   * Filters cases based on selected status filter
   * Returns all cases if 'All' is selected, otherwise filters by specific status
   * 
   * @returns {Array<Object>} Filtered array of cases
   */
  const getFilteredCases = () => {
    let filtered = cases;
    if (statusFilter !== 'All') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }
    if (countyFilter !== 'All') {
      filtered = filtered.filter(c => c.countyOfArrest === countyFilter);
    }
    return filtered;
  };

  /**
   * Gets unique statuses from all cases for filter options
   * 
   * @returns {Array<string>} Array of unique status values
   */
  const getUniqueStatuses = () => {
    const statuses = [...new Set(cases.map(c => c.status))];
    return statuses.sort();
  };

  // ==================== COMPONENT RENDERING ====================
  
  if (authState === 'checking' || authState === 'redirecting') {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'white' }}>
        <h2 style={{ color: 'white' }}>Authenticating...</h2>
        <p style={{ color: '#f1f5f9' }}>Please wait while we complete sign-in.</p>
      </div>
    );
  }

  if (authState === 'denied') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>Your account is authenticated but does not have the AppAdmin role.</p>
        <button onClick={() => navigate('/')} style={{ marginTop: '16px' }}>
          Return Home
        </button>
      </div>
    );
  }

  if (authState === 'error') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Authentication Error</h2>
        <p>{authError || 'Unable to complete authentication.'}</p>
        <button onClick={() => navigate('/')} style={{ marginTop: '16px' }}>
          Return Home
        </button>
      </div>
    );
  }

  /**
   * Admin Dashboard Component
   * Renders when user is successfully authenticated
   * 
   * Features:
   * - Case listing with status-based sorting
   * - Individual case status management
   * - Color-coded status indicators
   * - Real-time status updates
   * - Logout functionality
   */
  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      backgroundColor: '#303b51',
      minHeight: '100vh',
      padding: '20px'
    }}>
      {/* Email / Status Update Notification Popup */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '24px',
          right: '24px',
          zIndex: 9999,
          minWidth: '320px',
          maxWidth: '480px',
          backgroundColor: notification.type === 'success' ? '#d4edda' : '#f8d7da',
          border: `1px solid ${notification.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
          borderLeft: `5px solid ${notification.type === 'success' ? '#28a745' : '#dc3545'}`,
          borderRadius: '6px',
          padding: '16px 20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px',
          animation: 'slideIn 0.3s ease'
        }}>
          <span style={{ fontSize: '20px', flexShrink: 0 }}>
            {notification.type === 'success' ? '✅' : '❌'}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{
              fontWeight: '600',
              fontSize: '14px',
              color: notification.type === 'success' ? '#155724' : '#721c24',
              marginBottom: '4px'
            }}>
              {notification.type === 'success' ? 'Update Successful' : 'Update Failed'}
            </div>
            <div style={{
              fontSize: '13px',
              color: notification.type === 'success' ? '#155724' : '#721c24'
            }}>
              {notification.message}
            </div>
          </div>
          <button
            onClick={() => setNotification(null)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px',
              lineHeight: 1,
              color: notification.type === 'success' ? '#155724' : '#721c24',
              flexShrink: 0,
              padding: '0 2px'
            }}
            aria-label="Dismiss notification"
          >
            &times;
          </button>
        </div>
      )}
      {/* Dashboard Header */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px 30px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: '600',
            color: '#2c3e50'
          }}>
            Admin Dashboard
          </h1>
          {adminUser && (
            <p style={{
              margin: '4px 0 0 0',
              fontSize: '13px',
              color: '#6c757d'
            }}>
              Signed in as {adminUser.name || adminUser.username}
            </p>
          )}
        </div>
        <button 
          onClick={handleLogout} 
          style={{ 
            padding: '10px 20px', 
            backgroundColor: '#dc3545', 
            color: 'white', 
            border: 'none', 
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
        >
          Sign Out
        </button>
      </div>

      {casesLoading ? (
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '16px', color: '#6c757d' }}>Cases loading...</p>
        </div>
      ) : cases.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, fontSize: '16px', color: '#6c757d' }}>No cases submitted yet.</p>
        </div>
      ) : (
        <>
          {/* Filter and Summary Section */}
          <div style={{
            backgroundColor: 'white',
            padding: '20px 30px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '20px'
          }}>
            {/* Filter Controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#495057'
              }}>
                Filter by Status:
              </label>
              <select
                className="admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '2px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  color: '#495057',
                  cursor: 'pointer',
                  minWidth: '150px'
                }}
              >
                <option value="All">All Status ({cases.length})</option>
                {getUniqueStatuses().map(status => (
                  <option key={status} value={status}>
                    {status} ({cases.filter(c => c.status === status).length})
                  </option>
                ))}
              </select>
              <label style={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#495057'
              }}>
                Filter by County:
              </label>
              <select
                className="admin-select"
                value={countyFilter}
                onChange={(e) => setCountyFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '2px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white',
                  color: '#495057',
                  cursor: 'pointer',
                  minWidth: '150px'
                }}
              >
                <option value="All">All Counties ({cases.length})</option>
                {COUNTY_OF_ARREST_OPTIONS.map(county => (
                  <option key={county} value={county}>
                    {county} ({cases.filter(c => c.countyOfArrest === county).length})
                  </option>
                ))}
              </select>
            </div>

            {/* Summary Stats */}
            <div style={{ 
              display: 'flex', 
              gap: '20px', 
              fontSize: '14px',
              color: '#6c757d',
              flexWrap: 'wrap'
            }}>
              <div>
                <strong>Showing:</strong> {getFilteredCases().length} of {cases.length} cases
              </div>
              {statusFilter !== 'All' && (
                <div>
                  <strong>Status:</strong> {statusFilter}
                </div>
              )}
              {countyFilter !== 'All' && (
                <div>
                  <strong>County:</strong> {countyFilter}
                </div>
              )}
            </div>
          </div>

          {/* Cases Table */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            {/* Table Container */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                fontSize: '14px'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Status
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Tracking Number
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Role
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Name
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Email
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Payment ID
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Jail ID
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Police Report #
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      File Number
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      County of Arrest
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Updated By
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Update Status
                    </th>
                    <th style={{ 
                      padding: '16px 20px', 
                      textAlign: 'center', 
                      fontWeight: '600',
                      color: '#495057',
                      fontSize: '13px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredCases().map((c, index) => (
                    <tr 
                      key={c.caseNumber} 
                      style={{ 
                        borderBottom: '1px solid #f1f3f4',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '16px 20px' }}>
                        <span 
                          style={{ 
                            padding: '6px 12px', 
                            borderRadius: '20px', 
                            backgroundColor: getStatusColor(c.status),
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '500',
                            textTransform: 'lowercase'
                          }}
                        >
                          {c.status}
                        </span>
                      </td>
                      <td style={{ 
                        padding: '16px 20px',
                        fontFamily: 'monospace', 
                        fontSize: '13px',
                        color: '#495057',
                        fontWeight: '500'
                      }}>
                        {c.caseNumber}
                      </td>
                      <td style={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#495057',
                        fontWeight: '500'
                      }}>
                        {c.role}
                      </td>
                      <td style={{ 
                        padding: '16px 20px',
                        fontSize: '14px',
                        color: '#495057',
                        fontWeight: '500'
                      }}>
                        {c.firstName} {c.middleName || ''} {c.lastName}
                      </td>
                      <td style={{ 
                        padding: '16px 20px', 
                        fontSize: '13px',
                        color: '#495057'
                      }}>
                        {c.email || '-'}
                      </td>
                      <td style={{ 
                        padding: '16px 20px', 
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: '#6c757d'
                      }}>
                        {c.localReferenceId || '-'}
                      </td>
                      <td style={{ 
                        padding: '16px 20px', 
                        fontSize: '13px',
                        color: '#6c757d'
                      }}>
                        {c.jailId || '-'}
                      </td>
                      <td style={{ 
                        padding: '16px 20px', 
                        fontFamily: 'monospace', 
                        fontSize: '11px',
                        color: '#6c757d'
                      }}>
                        {c.policeReportNumber || '-'}
                      </td>
                      <td style={{ 
                        padding: '16px 20px', 
                        fontFamily: 'monospace', 
                        fontSize: '11px',
                        color: '#6c757d'
                      }}>
                        {c.fileNumber}
                      </td>
                      <td style={{ 
                        padding: '16px 20px', 
                        fontSize: '13px',
                        color: '#495057',
                        fontWeight: '500'
                      }}>
                        {c.countyOfArrest || '-'}
                      </td>
                      <td style={{ 
                        padding: '16px 20px', 
                        fontSize: '13px',
                        color: '#6c757d'
                      }}>
                        {c.updatedBy || '-'}
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <select
                          className="admin-status-select"
                          value={statusUpdates[c.caseNumber] || c.status}
                          onChange={(e) => handleStatusChange(c.caseNumber, e.target.value)}
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #dee2e6', 
                            borderRadius: '6px',
                            fontSize: '13px',
                            backgroundColor: 'white',
                            color: '#495057',
                            cursor: 'pointer',
                            minWidth: '120px'
                          }}
                        >
                          {CASE_STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleStatusSubmit(c.caseNumber, c.email)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: statusUpdates[c.caseNumber] === c.status ? '#6c757d' : '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: statusUpdates[c.caseNumber] === c.status ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '500',
                            transition: 'all 0.2s',
                            minWidth: '70px'
                          }}
                          disabled={statusUpdates[c.caseNumber] === c.status}
                          onMouseOver={(e) => {
                            if (!e.target.disabled) {
                              e.target.style.backgroundColor = '#218838';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!e.target.disabled) {
                              e.target.style.backgroundColor = '#28a745';
                            }
                          }}
                        >
                          Submit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* No Results Message */}
            {getFilteredCases().length === 0 && (statusFilter !== 'All' || countyFilter !== 'All') && (
              <div style={{
                padding: '40px',
                textAlign: 'center',
                color: '#6c757d',
                fontSize: '16px'
              }}>
                No cases found matching the selected filters.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Export AdminPage component as default
 * 
 * Usage in App.js:
 * import AdminPage from './components/AdminPage';
 * 
 * Route Configuration:
 * <Route path="/admin" element={<AdminPage />} />
 * 
 * Authentication:
 * - Microsoft 365 SSO via MSAL (@azure/msal-react)
 * - Backend validates JWT tokens via Microsoft.Identity.Web
 * - Requires "access_as_user" scope and "AppAdmin" role
 * 
 * API Dependencies:
 * - GET /case (fetch cases, bearer token required)
 * - POST /case/status (update case status, bearer token required)
 */
export default AdminPage;